"use client"

import { Button } from '@/app/components/ui/button';
import { Checkbox } from '@/app/components/ui/checkbox';
import { DrawerContent, Drawer, DrawerTrigger } from '@/app/components/material/drawer';
import { Flex, Text } from '@radix-ui/themes';
import React, { useContext, useState } from 'react';
import { AuctionFilterContext, Filter, FilterType } from '@/lib/context/AuctionFilterContext';
import { X } from 'lucide-react';

interface Props {
  allCategories: string[];
}

const CategoryFilter = ({ allCategories }: Props) => {
  const auctionFilterContext = useContext(AuctionFilterContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checked, setChecked] = useState<boolean[]>(
    Array(allCategories.length).fill(false)
  );

  const handleOpenChange = (val: boolean) => {
    if (val) {
      const cachedCategories: string[] = auctionFilterContext
        ?.getFiltersOfType(FilterType.CATEGORY)
        ?.map(f => f.label)
        || [];
      const loadedChecks = Array(allCategories.length).fill(false);

      if (!cachedCategories.length) {
        setChecked(loadedChecks);
      } else {
        for (let i = 0; i < loadedChecks.length; ++i) {
          loadedChecks[i] = cachedCategories.includes(allCategories[i]);
        }
        setChecked(loadedChecks); // Ensure setChecked is called to update the state
      }
    }

    setDrawerOpen(val)
  }

  const filterByCategory = () => {
    const newFilters: Filter[] = [];
    allCategories.forEach((item, index) => {
      if (checked[index]) {
        newFilters.push(
          {
            label: item,
            type: FilterType.CATEGORY
          }
        );
      }
    })
    auctionFilterContext?.updateFiltersOfType(newFilters, FilterType.CATEGORY);
  };

  const handleCheckboxChange = (id: number) => {
    setChecked(prev => {
      const newCategories = [...prev];
      newCategories[id] = !newCategories[id];
      return newCategories;
    })
  };

  const clearCheckboxes = () => {
    setChecked(
      Array(allCategories.length).fill(false)
    )
  }

  const selectAllCheckboxes = () => {
    setChecked(
      Array(allCategories.length).fill(true)
    )
  }

  return (
    <Drawer direction="right" open={drawerOpen} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>
        <Button variant="outline" className="rounded-none h-11">
          Category
        </Button>
      </DrawerTrigger>
      <DrawerContent className="py-6 px-3 min-w-[380px]">
        <Flex direction="column" className="w-full h-full">
          <Flex justify="between" align="center" className="w-full h-12 px-3">
            <Button className="invisible">
              <X className="w-4 h-4" />
            </Button>
            <Text className="text-lg font-semibold leading-none">
              Categories
            </Text>
            <Button variant="ghost" onClick={() => setDrawerOpen(false)}>
              <X className="w-4 h-4" />
            </Button>
          </Flex>
          <Flex justify="center" align="center" className="w-full h-12 px-3">
            <Button variant="ghost" className="font-normal" onClick={selectAllCheckboxes}>
              Select All
            </Button>
            <Button variant="ghost" className="font-normal" onClick={clearCheckboxes}>
              Clear
            </Button>
          </Flex>
          <Flex direction="column" gap="3" className="space-y-2 mt-5 h-[70dvh] overflow-y-auto ml-5 mr-2">
            {
              allCategories.map((item: string, id: number) => (
                <Flex key={item} className="space-x-3" align="center">
                  <Checkbox
                    checked={checked[id]}
                    onCheckedChange={() => handleCheckboxChange(id)}
                  />
                  <Text className="hover:cursor-pointer text-sm font-normal" onClick={() => handleCheckboxChange(id)}>
                    {item}
                  </Text>
                </Flex>
              ))
            }
          </Flex>
        </Flex>
        <Button className="h-10 mx-5" onClick={() => {
          filterByCategory();
          setDrawerOpen(false);
        }}>
          Submit
        </Button>
      </DrawerContent>
    </Drawer>
  );
}

export default CategoryFilter
